import { ComponentType } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { AuthState } from '@okta/okta-auth-js'

export interface RoleContext {
  authState: AuthState | null
  hasRole: (s: string) => boolean
  hasAnyRole: (...s: string[]) => boolean
  hasCustomerDetailsAccess: () => boolean
}

export const useRoleContext = (): RoleContext => {
  const { authState } = useOktaAuth()

  const hasRole = (role: string): boolean => {
    return !!authState?.accessToken?.claims['groups']?.toString()?.includes(role)
  }

  const hasAnyRole = (...roles: string[]): boolean => {
    for (const role of roles) {
      if (hasRole(role)) {
        return true
      }
    }
    return false
  }

  const hasCustomerDetailsAccess = (): boolean => {
    return hasRole('App.EMEA.ShipmentTracking.viewCustomerDetails')
  }

  return {
    hasRole,
    hasAnyRole,
    hasCustomerDetailsAccess,
    authState,
  }
}

export const withRoleContext = <P extends RoleContext>(Component: ComponentType<P>) => {
  return function WithRoleContextHoc(hocProps: Omit<P, keyof RoleContext>) {
    const role = useRoleContext()
    return <Component {...role} {...(hocProps as P)} />
  }
}

export default useRoleContext
